import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Search, Pencil, UserCog, KeyRound, Trash2, LogOut, X } from "lucide-react";
import { toast } from "sonner";
import { Button, Card, Badge, Logo } from "../components/kit";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ROLES = ["owner", "coach", "admin"];
const PLANS = ["vitals_core", "catalyst_free", "catalyst_pro", "catalyst_growth", "catalyst_scale", "catalyst_custom"];
const PLAN_STATUS = ["none", "trialing", "active", "canceled", "inactive", "past_due"];
const ROLE_TONE = { admin: "accent", coach: "brand", owner: "neutral" };

export default function Admin() {
  const navigate = useNavigate();
  const { setUser, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [plan, setPlan] = useState("");
  const [editing, setEditing] = useState(null);
  const [allCoaches, setAllCoaches] = useState([]);

  useEffect(() => {
    api.get("/admin/users?role=coach").then((r) => setAllCoaches(r.data.users)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (role) params.set("role", role);
      if (plan) params.set("plan", plan);
      const [u, s] = await Promise.all([
        api.get(`/admin/users?${params.toString()}`),
        api.get("/admin/stats"),
      ]);
      setUsers(u.data.users);
      setStats(s.data);
    } catch (e) {
      toast.error("Couldn't load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role, plan]);

  const coaches = useMemo(() => users.filter((u) => u.role === "coach" || u.role === "admin"), [users]); // eslint-disable-line no-unused-vars

  const impersonate = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/impersonate`);
      setUser(data);
      navigate(data.role === "coach" ? "/coach" : (data.onboarding_complete ? "/dashboard" : "/onboarding"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't impersonate.");
    }
  };

  const resetPw = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      toast.success("Reset link sent", { description: data.message });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send reset.");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This permanently removes the account and all of its data.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Account deleted", { description: u.email });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't delete.");
    }
  };

  return (
    <div className="buktu-root" data-testid="admin-page" style={{ minHeight: "100vh", background: "var(--surface-page)" }}>
      <div className="buktu-wrap" style={{ maxWidth: 1180, paddingTop: "var(--space-8)", paddingBottom: "var(--space-20)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-6)" }}>
          <Logo height={28} to="vitals" />
          <Badge tone="accent" dot>Admin</Badge>
          <div style={{ marginLeft: "auto" }}>
            <Button variant="ghost" size="sm" data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/login"); }} iconLeft={<LogOut size={16} />}>Log out</Button>
          </div>
        </div>

        <h1 style={{ font: "var(--type-title)", letterSpacing: "var(--tracking-snug)", marginBottom: "var(--space-5)" }}>Users &amp; coaches</h1>

        {stats && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: "var(--space-3)", marginBottom: "var(--space-6)" }} className="buktu-admin-stats">
            <Stat label="Total" value={stats.total} testid="admin-stat-total" />
            <Stat label="Owners" value={stats.owners} testid="admin-stat-owners" />
            <Stat label="Coaches" value={stats.coaches} testid="admin-stat-coaches" />
            <Stat label="Admins" value={stats.admins} testid="admin-stat-admins" />
            <Stat label="Active subs" value={stats.active_subscriptions} testid="admin-stat-subs" />
          </div>
        )}

        <Card padding="var(--space-5)" style={{ marginBottom: "var(--space-5)" }}>
          <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "center" }}>
            <form onSubmit={(e) => { e.preventDefault(); load(); }} style={{ position: "relative", flex: 1, minWidth: 220 }}>
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-faint)", display: "grid", placeItems: "center" }}><Search size={16} /></span>
              <input data-testid="admin-search-input" className="buktu-input" style={{ paddingLeft: 38 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email…" />
            </form>
            <select data-testid="admin-role-filter" className="buktu-input" style={{ maxWidth: 160 }} value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="">All roles</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select data-testid="admin-plan-filter" className="buktu-input" style={{ maxWidth: 170 }} value={plan} onChange={(e) => setPlan(e.target.value)}>
              <option value="">All plans</option>
              {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <Button size="sm" data-testid="admin-search-btn" onClick={load}>Search</Button>
          </div>
        </Card>

        <Card padding="0" data-testid="admin-users-card" style={{ overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", font: "var(--type-body-sm)", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--surface-brand-wash)", textAlign: "left" }}>
                  {["User", "Role", "Plan", "Cohort / Coach", "Last active", "Actions"].map((h) => (
                    <th key={h} style={{ padding: "12px 14px", font: "var(--type-label)", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} style={{ padding: 24, color: "var(--text-muted)" }}>Loading…</td></tr>}
                {!loading && users.length === 0 && <tr><td colSpan={6} style={{ padding: 24, color: "var(--text-muted)" }}>No users match.</td></tr>}
                {!loading && users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-row-${u.id}`} style={{ borderTop: "1px solid var(--border-hairline)" }}>
                    <td style={{ padding: "12px 14px" }}>
                      <div style={{ font: "var(--type-label)", color: "var(--text-strong)" }}>{u.name || "—"}</div>
                      <div style={{ color: "var(--text-muted)", fontSize: 12 }}>{u.email}</div>
                    </td>
                    <td style={{ padding: "12px 14px" }}><Badge tone={ROLE_TONE[u.role] || "neutral"}>{u.role}</Badge></td>
                    <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                      <div>{u.plan}</div>
                      <div style={{ color: "var(--text-muted)", fontSize: 12 }}>{u.plan_status}</div>
                    </td>
                    <td style={{ padding: "12px 14px", color: "var(--text-body)" }}>
                      {u.role === "coach" || u.role === "admin"
                        ? <span>{u.cohort_name || "—"} <span style={{ color: "var(--text-muted)" }}>({u.cohort_size ?? 0})</span></span>
                        : (u.coach_name || <span style={{ color: "var(--text-muted)" }}>—</span>)}
                    </td>
                    <td style={{ padding: "12px 14px", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{u.last_active || "—"}</td>
                    <td style={{ padding: "12px 14px" }}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <IconBtn testid={`admin-edit-${u.id}`} title="Edit" onClick={() => setEditing(u)}><Pencil size={15} /></IconBtn>
                        <IconBtn testid={`admin-impersonate-${u.id}`} title="View as" onClick={() => impersonate(u)}><UserCog size={15} /></IconBtn>
                        <IconBtn testid={`admin-reset-${u.id}`} title="Send reset link" onClick={() => resetPw(u)}><KeyRound size={15} /></IconBtn>
                        <IconBtn testid={`admin-delete-${u.id}`} title="Delete" danger onClick={() => del(u)}><Trash2 size={15} /></IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <InquiriesCard />
      </div>

      {editing && (
        <EditModal
          user={editing}
          coaches={allCoaches}
          onClose={() => setEditing(null)}
          onSaved={(updated) => { setEditing(null); load(); }}
        />
      )}
      <style>{`@media (max-width:720px){.buktu-admin-stats{grid-template-columns:repeat(2,1fr) !important;}}`}</style>
    </div>
  );
}

function InquiriesCard() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/inquiries").then((r) => setRows(r.data.inquiries)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/admin/inquiries/${id}`, { status }); load(); };
  return (
    <Card padding="var(--space-5)" data-testid="admin-inquiries-card" style={{ marginTop: "var(--space-5)" }}>
      <h3 style={{ font: "var(--type-heading)", marginBottom: "var(--space-3)" }}>Catalyst Custom enquiries</h3>
      {!rows ? <p style={{ color: "var(--text-muted)" }}>Loading…</p> : rows.length === 0 ? <p data-testid="admin-inquiries-empty" style={{ font: "var(--type-body-sm)", color: "var(--text-faint)" }}>No enquiries yet.</p> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((q) => (
            <div key={q.id} data-testid={`admin-inquiry-${q.id}`} style={{ padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-hairline)", font: "var(--type-body-sm)", fontSize: 13 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <b style={{ color: "var(--text-strong)" }}>{q.name}</b><span style={{ color: "var(--text-muted)" }}>· {q.organization} · {q.email} · ~{q.participants} participants</span>
                <select className="buktu-input" data-testid={`admin-inquiry-status-${q.id}`} value={q.status} onChange={(e) => setStatus(q.id, e.target.value)} style={{ marginLeft: "auto", maxWidth: 140, padding: "4px 8px" }}>
                  {["new", "contacted", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <p style={{ color: "var(--text-body)", marginTop: 6, whiteSpace: "pre-wrap" }}>{q.needs}</p>
              <span style={{ fontSize: 11, color: "var(--text-faint)" }}>{new Date(q.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value, testid }) {
  return (
    <Card padding="var(--space-4)" data-testid={testid}>
      <div style={{ font: "var(--type-display)", fontSize: 28, color: "var(--buktu-green)" }}>{value}</div>
      <div className="buktu-eyebrow">{label}</div>
    </Card>
  );
}

function IconBtn({ children, onClick, title, testid, danger }) {
  return (
    <button data-testid={testid} title={title} onClick={onClick}
      style={{ display: "grid", placeItems: "center", width: 30, height: 30, borderRadius: 8, cursor: "pointer",
        border: "1px solid var(--border-hairline)", background: "var(--surface-card)",
        color: danger ? "var(--status-risk)" : "var(--text-body)" }}>
      {children}
    </button>
  );
}

function EditModal({ user, coaches, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    role: user.role,
    plan: user.plan,
    plan_status: user.plan_status,
    timezone: user.timezone || "UTC",
    coach_privacy: user.coach_privacy || "anonymous",
    cohort_name: user.cohort_name || "",
    coach_id: user.coach_id || "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setError(""); setSaving(true);
    try {
      const { data } = await api.patch(`/admin/users/${user.id}`, form);
      toast.success("User updated", { description: data.email });
      onSaved(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save.");
    } finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15,30,25,0.45)", display: "grid", placeItems: "center", padding: "var(--space-4)" }}>
      <div data-testid="admin-edit-modal" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", background: "var(--surface-card)", borderRadius: "var(--radius-lg)", padding: "var(--space-6)", boxShadow: "var(--shadow-lg)" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "var(--space-4)" }}>
          <h3 style={{ font: "var(--type-heading)" }}>Edit user</h3>
          <button data-testid="admin-edit-close" onClick={onClose} style={{ marginLeft: "auto", background: "none", border: 0, cursor: "pointer", color: "var(--text-muted)" }}><X size={20} /></button>
        </div>
        <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)", marginBottom: "var(--space-5)" }}>{user.email}</p>

        {error && <div data-testid="admin-edit-error" style={{ padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--status-risk-soft)", color: "#8a2a1a", font: "var(--type-body-sm)", fontSize: 13, marginBottom: "var(--space-4)" }}>{error}</div>}

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          <L label="Name"><input data-testid="admin-edit-name" className="buktu-input" value={form.name} onChange={(e) => set("name", e.target.value)} /></L>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
            <L label="Role">
              <select data-testid="admin-edit-role" className="buktu-input" value={form.role} onChange={(e) => set("role", e.target.value)}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </L>
            <L label="Timezone"><input data-testid="admin-edit-timezone" className="buktu-input" value={form.timezone} onChange={(e) => set("timezone", e.target.value)} /></L>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
            <L label="Plan">
              <select data-testid="admin-edit-plan" className="buktu-input" value={form.plan} onChange={(e) => set("plan", e.target.value)}>
                {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </L>
            <L label="Plan status">
              <select data-testid="admin-edit-plan-status" className="buktu-input" value={form.plan_status} onChange={(e) => set("plan_status", e.target.value)}>
                {PLAN_STATUS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </L>
          </div>

          {(form.role === "coach" || form.role === "admin") && (
            <L label="Cohort name"><input data-testid="admin-edit-cohort" className="buktu-input" value={form.cohort_name} onChange={(e) => set("cohort_name", e.target.value)} /></L>
          )}
          {form.role === "owner" && (
            <>
              <L label="Assigned coach">
                <select data-testid="admin-edit-coach" className="buktu-input" value={form.coach_id} onChange={(e) => set("coach_id", e.target.value)}>
                  <option value="">— None —</option>
                  {coaches.map((c) => <option key={c.id} value={c.id}>{c.name || c.email} {c.cohort_name ? `(${c.cohort_name})` : ""}</option>)}
                </select>
              </L>
              <L label="Coach privacy">
                <select data-testid="admin-edit-privacy" className="buktu-input" value={form.coach_privacy} onChange={(e) => set("coach_privacy", e.target.value)}>
                  <option value="full">Full</option>
                  <option value="score_only">Score only</option>
                  <option value="anonymous">Anonymous</option>
                </select>
              </L>
            </>
          )}
        </div>

        <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-6)" }}>
          <Button variant="secondary" data-testid="admin-edit-cancel" onClick={onClose}>Cancel</Button>
          <Button data-testid="admin-edit-save" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
        </div>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", font: "var(--type-label)", color: "var(--text-strong)" }}>
      {label}
      {children}
    </label>
  );
}
